import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatPhoneNumber, formatZipCode, formatState, validatePhoneNumber, validateZipCode, validateState, US_STATES } from '../lib/validation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Search, User, Calendar, MapPin, Plus, LogOut, Loader2, ChevronRight, Shield, AlertCircle } from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";

export default function ClientSearchPage() {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showNewClient, setShowNewClient] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newClient, setNewClient] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    medicaid_id: ''
  });
  const [newClientErrors, setNewClientErrors] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async (search = '') => {
    setIsLoading(true);
    try {
      const data = await clientsAPI.getAll(search);
      setClients(data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    const timeoutId = setTimeout(() => {
      loadClients(value);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const handleSelectClient = (clientId) => {
    navigate(`/clients/${clientId}/packet`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNewClientChange = (field, value) => {
    let formattedValue = value;
    
    // Format based on field type
    if (field === 'phone') {
      formattedValue = formatPhoneNumber(value);
    } else if (field === 'zip_code') {
      formattedValue = formatZipCode(value);
    } else if (field === 'state') {
      formattedValue = formatState(value);
    }
    
    setNewClient(prev => ({ ...prev, [field]: formattedValue }));
    
    // Validate
    let validation = { valid: true, message: '' };
    if (field === 'phone') {
      validation = validatePhoneNumber(formattedValue);
    } else if (field === 'zip_code') {
      validation = validateZipCode(formattedValue);
    } else if (field === 'state') {
      validation = validateState(formattedValue);
    }
    
    setNewClientErrors(prev => {
      if (validation.valid) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [field]: validation.message };
    });
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    if (!newClient.first_name || !newClient.last_name || !newClient.dob) {
      toast.error('Please fill in name and date of birth');
      return;
    }

    // Validate all fields
    const errors = {};
    const phoneVal = validatePhoneNumber(newClient.phone);
    if (!phoneVal.valid) errors.phone = phoneVal.message;
    const zipVal = validateZipCode(newClient.zip_code);
    if (!zipVal.valid) errors.zip_code = zipVal.message;
    const stateVal = validateState(newClient.state);
    if (!stateVal.valid) errors.state = stateVal.message;
    
    if (Object.keys(errors).length > 0) {
      setNewClientErrors(errors);
      toast.error('Please fix validation errors');
      return;
    }

    setIsCreating(true);
    try {
      const created = await clientsAPI.create(newClient);
      toast.success('Client created!');
      setShowNewClient(false);
      setNewClient({
        first_name: '',
        last_name: '',
        dob: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        email: '',
        medicaid_id: ''
      });
      navigate(`/clients/${created.id}/packet`);
    } catch (error) {
      toast.error('Failed to create client');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="header-gradient sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={AVIDA_LOGO} 
                alt="Avida" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="font-bold text-xl text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Form Filler
                </h1>
                <p className="text-sm text-blue-600 font-medium">Welcome, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user?.is_admin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid="admin-button"
                >
                  <Shield className="w-4 h-4 mr-1" />
                  Admin
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-blue-600 hover:text-blue-800 hover:bg-amber-100 rounded-xl"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Search + New Client Row */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-400" />
            <Input
              type="text"
              placeholder="Search by name or DOB..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-12 h-14 text-lg input-styled"
              data-testid="client-search-input"
            />
          </div>
          <Button
            onClick={() => setShowNewClient(true)}
            className="h-14 px-5 btn-avida"
            data-testid="new-client-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">New Client</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>

        {/* Client List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-blue-600 font-medium">Loading clients...</p>
              </div>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-white shadow-lg flex items-center justify-center mb-4">
                <User className="w-10 h-10 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {searchQuery ? 'No clients found' : 'No clients yet'}
              </h3>
              <p className="text-blue-600 mb-5">
                {searchQuery ? 'Try a different search' : 'Add your first client to get started'}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => setShowNewClient(true)}
                  className="btn-avida px-6 h-12"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Add New Client
                </Button>
              )}
            </div>
          ) : (
            clients.map((client, index) => (
              <Card
                key={client.id}
                className="card-elevated p-5 cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 30}ms` }}
                onClick={() => handleSelectClient(client.id)}
                data-testid={`client-card-${client.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center flex-shrink-0 shadow-inner">
                      <User className="w-7 h-7 text-blue-700" />
                    </div>
                    <div>
                      <h3 
                        className="font-bold text-lg text-blue-900"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        {client.first_name} {client.last_name}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1">
                        <span className="flex items-center text-sm text-blue-500 font-medium">
                          <Calendar className="w-4 h-4 mr-1.5" />
                          {client.dob}
                        </span>
                        {client.city && (
                          <span className="flex items-center text-sm text-blue-500 font-medium">
                            <MapPin className="w-4 h-4 mr-1.5" />
                            {client.city}, {client.state}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* New Client Modal */}
      <Dialog open={showNewClient} onOpenChange={setShowNewClient}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-900 font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              New Client
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-float">First Name *</Label>
                <Input
                  value={newClient.first_name}
                  onChange={(e) => handleNewClientChange('first_name', e.target.value)}
                  className="input-styled"
                  placeholder="Maria"
                  required
                  data-testid="new-client-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-float">Last Name *</Label>
                <Input
                  value={newClient.last_name}
                  onChange={(e) => handleNewClientChange('last_name', e.target.value)}
                  className="input-styled"
                  placeholder="Garcia"
                  required
                  data-testid="new-client-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-float">Date of Birth *</Label>
              <Input
                type="date"
                value={newClient.dob}
                onChange={(e) => handleNewClientChange('dob', e.target.value)}
                className="input-styled"
                required
                data-testid="new-client-dob"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-float">Address</Label>
              <Input
                value={newClient.address}
                onChange={(e) => handleNewClientChange('address', e.target.value)}
                className="input-styled"
                placeholder="123 Main St"
                data-testid="new-client-address"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1 space-y-2">
                <Label className="label-float">City</Label>
                <Input
                  value={newClient.city}
                  onChange={(e) => handleNewClientChange('city', e.target.value)}
                  className="input-styled"
                  placeholder="Houston"
                  data-testid="new-client-city"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-float">State</Label>
                <Select
                  value={newClient.state}
                  onValueChange={(value) => handleNewClientChange('state', value)}
                >
                  <SelectTrigger 
                    className={`input-styled ${newClientErrors.state ? 'border-red-500' : ''}`}
                    data-testid="new-client-state"
                  >
                    <SelectValue placeholder="TX" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newClientErrors.state && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {newClientErrors.state}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="label-float">Zip</Label>
                <Input
                  value={newClient.zip_code}
                  onChange={(e) => handleNewClientChange('zip_code', e.target.value)}
                  className={`input-styled ${newClientErrors.zip_code ? 'border-red-500' : ''}`}
                  placeholder="77001"
                  data-testid="new-client-zip"
                />
                {newClientErrors.zip_code && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {newClientErrors.zip_code}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-float">Phone</Label>
              <Input
                type="tel"
                value={newClient.phone}
                onChange={(e) => handleNewClientChange('phone', e.target.value)}
                className={`input-styled ${newClientErrors.phone ? 'border-red-500' : ''}`}
                placeholder="(713) 555-0123"
                data-testid="new-client-phone"
              />
              {newClientErrors.phone && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {newClientErrors.phone}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="label-float">Medicaid ID</Label>
              <Input
                value={newClient.medicaid_id}
                onChange={(e) => handleNewClientChange('medicaid_id', e.target.value)}
                className="input-styled"
                placeholder="TX123456789"
                data-testid="new-client-medicaid"
              />
            </div>

            <div className="flex gap-3 pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewClient(false)}
                className="flex-1 h-12 border-2 border-amber-200 text-blue-800 font-semibold rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isCreating}
                className="flex-1 h-12 btn-avida"
                data-testid="create-client-button"
              >
                {isCreating ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Plus className="w-5 h-5 mr-2" />
                )}
                Create & Start
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
